/**
 * Timer Notifications Service
 *
 * Handles scheduling and canceling local notifications for the round timer.
 * This allows the timer to alert users even when the app is backgrounded,
 * without requiring UIBackgroundModes audio.
 */

import { Capacitor } from '@capacitor/core';
import { getLocalNotificationsModule } from './notifications.helper';
import { TimerSegment } from '../types/round-timer';
import logger from '../utils/logger';

// Base ID for timer notifications - we'll add segment index to make unique IDs
const TIMER_NOTIFICATION_BASE_ID = 900000;

// Storage key for tracking scheduled notification IDs
const TIMER_NOTIFICATION_IDS_KEY = 'TIMER_NOTIFICATION_IDS';

/**
 * Check if we're on a native platform
 */
const isNativePlatform = (): boolean => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

/**
 * Request notification permissions if not already granted
 */
export const requestTimerNotificationPermissions = async (): Promise<boolean> => {
  if (!isNativePlatform()) {
    return true; // Web doesn't need permission for our use case
  }

  try {
    const { LocalNotifications } = await getLocalNotificationsModule();

    // Check current permission status
    const permStatus = await LocalNotifications.checkPermissions();

    if (permStatus.display === 'granted') {
      return true;
    }

    // Request permissions
    const result = await LocalNotifications.requestPermissions();
    return result.display === 'granted';
  } catch (error) {
    logger.error('[TimerNotifications] Error requesting permissions:', error);
    return false;
  }
};

/**
 * Schedule notifications for all timer segments
 *
 * @param segments - Array of timer segments
 * @param startFromIndex - Which segment to start scheduling from (for resume)
 * @param currentSegmentTimeLeft - Time left in current segment (for resume)
 */
export const scheduleTimerNotifications = async (
  segments: TimerSegment[],
  startFromIndex: number = 0,
  currentSegmentTimeLeft?: number
): Promise<void> => {
  if (!isNativePlatform()) {
    logger.info('[TimerNotifications] Skipping on web platform');
    return;
  }

  try {
    const { LocalNotifications } = await getLocalNotificationsModule();

    // First, cancel any existing timer notifications
    await cancelTimerNotifications();

    const now = Date.now();
    const notifications: Array<{
      id: number;
      title: string;
      body: string;
      schedule: { at: Date };
      sound: string;
      extra: { type: string; segmentIndex: number };
    }> = [];

    let cumulativeTime = 0;
    const scheduledIds: number[] = [];

    // Calculate cumulative time for segments before startFromIndex
    // (these have already completed)
    for (let i = 0; i < startFromIndex; i++) {
      // Don't add time for completed segments
    }

    // If we're resuming mid-segment, use the provided time left
    if (currentSegmentTimeLeft !== undefined && startFromIndex < segments.length) {
      cumulativeTime = currentSegmentTimeLeft;
    } else if (startFromIndex < segments.length) {
      cumulativeTime = segments[startFromIndex].time;
    }

    // Schedule notification for current segment completion
    if (startFromIndex < segments.length) {
      const currentSegment = segments[startFromIndex];
      const notificationId = TIMER_NOTIFICATION_BASE_ID + startFromIndex;
      const nextSegment = segments[startFromIndex + 1];

      notifications.push({
        id: notificationId,
        title: getSegmentEndTitle(currentSegment),
        body: nextSegment
          ? `Next: ${nextSegment.label}`
          : 'Timer complete! Great workout!',
        schedule: { at: new Date(now + cumulativeTime * 1000) },
        sound: 'default',
        extra: {
          type: 'timerSegment',
          segmentIndex: startFromIndex
        }
      });
      scheduledIds.push(notificationId);
    }

    // Schedule notifications for remaining segments
    for (let i = startFromIndex + 1; i < segments.length; i++) {
      const segment = segments[i];
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const prevSegment = segments[i - 1];

      // Add time of previous segment
      if (i === startFromIndex + 1 && currentSegmentTimeLeft !== undefined) {
        cumulativeTime = currentSegmentTimeLeft;
      } else if (i > startFromIndex + 1) {
        cumulativeTime += segments[i - 1].time;
      }

      // Add current segment time to get when it ends
      cumulativeTime += segment.time;

      const notificationId = TIMER_NOTIFICATION_BASE_ID + i;
      const nextSegment = segments[i + 1];

      notifications.push({
        id: notificationId,
        title: getSegmentEndTitle(segment),
        body: nextSegment
          ? `Next: ${nextSegment.label}`
          : 'Timer complete! Great workout!',
        schedule: { at: new Date(now + cumulativeTime * 1000) },
        sound: 'default',
        extra: {
          type: 'timerSegment',
          segmentIndex: i
        }
      });
      scheduledIds.push(notificationId);
    }

    // Schedule all notifications
    if (notifications.length > 0) {
      await LocalNotifications.schedule({ notifications });

      // Store IDs for later cancellation
      localStorage.setItem(TIMER_NOTIFICATION_IDS_KEY, JSON.stringify(scheduledIds));

      logger.info(`[TimerNotifications] Scheduled ${notifications.length} notifications`);
    }
  } catch (error) {
    logger.error('[TimerNotifications] Error scheduling notifications:', error);
  }
};

/**
 * Cancel all timer notifications
 */
export const cancelTimerNotifications = async (): Promise<void> => {
  if (!isNativePlatform()) {
    return;
  }

  try {
    const { LocalNotifications } = await getLocalNotificationsModule();

    // Get stored notification IDs
    const storedIds = localStorage.getItem(TIMER_NOTIFICATION_IDS_KEY);

    if (storedIds) {
      const ids: number[] = JSON.parse(storedIds);

      if (ids.length > 0) {
        await LocalNotifications.cancel({
          notifications: ids.map(id => ({ id }))
        });

        logger.info(`[TimerNotifications] Cancelled ${ids.length} notifications`);
      }

      // Clear stored IDs
      localStorage.removeItem(TIMER_NOTIFICATION_IDS_KEY);
    }
  } catch (error) {
    logger.error('[TimerNotifications] Error cancelling notifications:', error);
  }
};

/**
 * Get appropriate title for segment end notification
 * Note: TimerSegmentType is 'round' | 'rest' | 'extra'
 * Warm up and cool down are both 'extra' type, distinguished by label
 */
const getSegmentEndTitle = (segment: TimerSegment): string => {
  switch (segment.type) {
    case 'round':
      return 'Round Complete!';
    case 'rest':
      return 'Rest Over!';
    case 'extra':
      // Extra covers both warm up and cool down - use label to distinguish
      if (segment.label.toLowerCase().includes('warm')) {
        return 'Warm Up Complete!';
      } else if (segment.label.toLowerCase().includes('cool')) {
        return 'Cool Down Complete!';
      }
      return 'Segment Complete!';
    default:
      return 'Segment Complete!';
  }
};

/**
 * Schedule a single completion notification for when the entire timer finishes
 */
export const scheduleTimerCompletionNotification = async (
  totalTimeSeconds: number
): Promise<void> => {
  if (!isNativePlatform()) {
    return;
  }

  try {
    const { LocalNotifications } = await getLocalNotificationsModule();

    const notificationId = TIMER_NOTIFICATION_BASE_ID + 999; // Special ID for completion

    await LocalNotifications.schedule({
      notifications: [{
        id: notificationId,
        title: 'Timer Complete!',
        body: 'Great workout! Your training session is finished.',
        schedule: { at: new Date(Date.now() + totalTimeSeconds * 1000) },
        sound: 'default',
        extra: {
          type: 'timerComplete'
        }
      }]
    });

    // Add to stored IDs
    const storedIds = localStorage.getItem(TIMER_NOTIFICATION_IDS_KEY);
    const ids: number[] = storedIds ? JSON.parse(storedIds) : [];
    ids.push(notificationId);
    localStorage.setItem(TIMER_NOTIFICATION_IDS_KEY, JSON.stringify(ids));

    logger.info('[TimerNotifications] Scheduled completion notification');
  } catch (error) {
    logger.error('[TimerNotifications] Error scheduling completion notification:', error);
  }
};

const timerNotificationsService = {
  requestTimerNotificationPermissions,
  scheduleTimerNotifications,
  cancelTimerNotifications,
  scheduleTimerCompletionNotification
};

export default timerNotificationsService;
